import type { CommonParams, MetaData } from '../../../../types/global.types';

/* ------------------------------------------------------------------ */
/* Pay type (JTI)                                                      */
/* An employee is paid either a monthly salary or a per-day rate.      */
/* Salary structure, grade and payroll cycle are all scoped by this.   */
/* ------------------------------------------------------------------ */
export type PayType = 'MONTHLY' | 'DAILY';

/** Query arg for the structure/grade queries. `null` means MONTHLY (legacy call sites). */
export type PayTypeArg = PayType | null | undefined;

export const DEFAULT_PAY_TYPE: PayType = 'MONTHLY';

export const resolvePayType = (arg: PayTypeArg): PayType =>
  arg === 'DAILY' ? 'DAILY' : DEFAULT_PAY_TYPE;

export const PAY_TYPE_OPTIONS: { label: string; value: PayType }[] = [
  { label: 'Monthly', value: 'MONTHLY' },
  { label: 'Daily', value: 'DAILY' },
];

// Salary Structure Types
export interface SalaryStructureItem {
  id: string;
  payroll_id: string;
  organization_id: string;
  tenant_id: string;
  component_name: string;
  component_percentage: string;
  created_at: string;
  updated_at: string;
}

export interface SalaryStructureData {
  /** Which population this structure belongs to. */
  pay_basis?: PayType;
  id: string;
  organization_id: string;
  tenant_id: string;
  /** JTI: absent on legacy responses, treated as MONTHLY. */
  pay_type?: PayType;
  salary_structure_effective_date: string;
  created_at: string;
  updated_at: string;
  salary_structures: SalaryStructureItem[];
}

/** Body sent to `POST /payroll/salary-structure/upsert`. */
export interface UpdateSalaryStructureBody {
  payBasis: PayType;
  organizationId?: string;
  tenantId?: string;
  /** JTI: omitted for MONTHLY so the legacy contract is unchanged. */
  payType?: PayType;
  salaryStructureEffectiveDate: string;
  salaryStructures: { componentName: string; componentPercentage: number }[];
}

export interface SalaryStructureResponse {
  success: boolean;
  message: string;
  data: SalaryStructureData;
}

// Salary Grade Types
export interface BenefitItem {
  id: string;
  grade_list_id: string;
  organization_id: string;
  tenant_id: string;
  benefit_name: string;
  is_tax_applicable: boolean;
  fixed_amount_bdt: string | null;
  percentage: string | null;
  percentage_of: string | null;
  created_at: string;
  updated_at: string;
}

export interface GradeItem {
  id: string;
  payroll_grade_id: string;
  organization_id: string;
  tenant_id: string;
  grade_name: string;
  /** MONTHLY only. Daily grades send `'0'` here. */
  min_salary_bdt: string;
  /** MONTHLY only. Daily grades send `'0'` here. */
  max_salary_bdt: string;
  /** DAILY only — the flat per-day rate. */
  daily_rate_bdt?: string | null;
  created_at: string;
  updated_at: string;
  benefits: BenefitItem[];
}

export interface SalaryGradeData {
  id: string;
  organization_id: string;
  tenant_id: string;
  /** Which population this grade set belongs to. */
  pay_basis?: PayType;
  grade_effective_date: string;
  created_at: string;
  updated_at: string;
  grades: GradeItem[];
}

/** One grade inside the `POST /payroll/salary-grade/upsert` body. */
export interface UpdateGradeItemBody {
  gradeName: string;
  /** MONTHLY only. */
  minSalary?: number;
  /** MONTHLY only. */
  maxSalary?: number;
  /** DAILY only — the flat per-day rate. */
  dailyRateBdt?: number;
  benefits: {
    benefitName: string;
    isTaxApplicable: boolean;
    fixedAmount?: number;
    percentage?: number;
    percentageOf?: string;
  }[];
}

/** Body sent to `POST /payroll/salary-grade/upsert`. */
export interface UpdateSalaryGradeBody {
  organizationId?: string;
  tenantId?: string;
  payBasis: PayType;
  gradeEffectiveDate: string;
  grades: UpdateGradeItemBody[];
}

/* ------------------------------------------------------------------ */
/* Payroll Calendar (JTI)                                              */
/* Configured under the Salary Structure & Grade page. Monthly and     */
/* daily employees each get their own cycle.                           */
/* ------------------------------------------------------------------ */
export type PayrollCycleType =
  | 'CALENDAR_MONTH'
  | 'CUSTOM_MONTH'
  | 'FIXED_INTERVAL';

/**
 * One population's cycle, exactly as `GET /payroll-calendar` returns it.
 * The same shape is what `payroll-cycle.utils` resolves into real dates.
 */
export interface PayrollCycleConfig {
  calendar_name?: string;
  cycle_type: PayrollCycleType;
  /** CUSTOM_MONTH and FIXED_INTERVAL: day of month the cycle opens (1-31). */
  start_on_date?: number | null;
  /** FIXED_INTERVAL: cycle length in days (e.g. 15). */
  cycle_length_days?: number | null;
  /** False until the company saves a calendar for this population. */
  configured?: boolean;
  effective_date?: string | null;
}

export interface PayrollCalendarResponse {
  success: boolean;
  message: string | null;
  data: {
    pay_basis: PayType;
    calendar: PayrollCycleConfig;
  };
}

/** Body sent to `POST /payroll-calendar`. One population per call. */
export interface UpdatePayrollCalendarBody {
  payBasis: PayType;
  cycleType: PayrollCycleType;
  /** Always sent. A calendar month opens on the 1st. */
  startOnDate: number;
  /** Required for FIXED_INTERVAL. */
  cycleLengthDays?: number;
  effectiveDate: string;
}

/** One cycle of a month, as `GET /payroll-calendar/cycles` returns it. */
export interface PayrollCycleOption {
  cycleNo: number;
  startDate: string;
  endDate: string;
  totalDates: number;
  label: string;
}

export interface PayrollCyclesResponse {
  success: boolean;
  message: string | null;
  data: {
    pay_basis: PayType;
    year: number;
    month: string;
    cycles: PayrollCycleOption[];
  };
}

/** Safe default — reproduces today's behaviour when nothing is configured. */
export const DEFAULT_PAYROLL_CYCLE: PayrollCycleConfig = {
  cycle_type: 'CALENDAR_MONTH',
};

/* ------------------------------------------------------------------ */
/* Daily Pay Sheet (JTI)                                              */
/* One yes/no decision per daily employee per day of the cycle.        */
/* ------------------------------------------------------------------ */

/** A single day cell. The API answers present or absent, never unset. */
export interface DailyPayEntry {
  /** `YYYY-MM-DD`. */
  date: string;
  is_payable: boolean | null;
  note?: string | null;
}

/** One row of the grid: an employee plus their entries for the cycle. */
export interface DailyPayRow {
  user_id: string;
  employee_id: string;
  name: string;
  email?: string;
  photo?: string | null;
  department_name?: string | null;
  designation_name?: string | null;
  daily_rate: string | number;
  entries: DailyPayEntry[];
}

/** One employee row of `GET /payroll-calendar/per-sheet`. */
export interface PerSheetEmployee {
  user_id: string;
  employee_id: string;
  name: string;
  email?: string;
  grade?: string | null;
  daily_rate_bdt?: number | string | null;
  /** The rate actually used, which can come from the grade. */
  effective_daily_rate?: number | string | null;
  gross_amount?: number;
  present_dates: string[];
  absent_dates: string[];
  present_count: number;
  absent_count: number;
}

export interface PerSheetParams {
  payBasis: PayType;
  year: number | string;
  month: string;
}

export interface PerSheetResponse {
  success: boolean;
  message: string | null;
  data: {
    pay_basis: PayType;
    year: number;
    month: string;
    calendar: PayrollCycleConfig;
    /** Every cycle the month splits into. */
    cycles: PayrollCycleOption[];
    /** Every date of the month covered by those cycles. */
    dates: string[];
    total_employees: number;
    total_present: number;
    total_absent: number;
    total_gross_amount: number;
    employees: PerSheetEmployee[];
  };
}

/** One toggle inside `PATCH /payroll-calendar/per-sheet/bulk`. */
export interface PerSheetDayUpdate {
  userId: string;
  /** `YYYY-MM-DD`. */
  date: string;
  isPresent: boolean;
}

/** What an upload would do, worked out in the browser before it is sent. */
export interface DailyPayValueChange {
  employee_id: string;
  name: string;
  date: string;
  from: boolean;
  to: boolean;
}

export interface DailyPayUploadPreview {
  employees: number;
  days: number;
  entries: number;
  overwrites: number;
  failed: number;
  reasons: string[];
  changes: DailyPayValueChange[];
}

/** One rejected row from a daily pay Excel upload. */
export interface FailedDailyPayRecord {
  id: string;
  job_id: string;
  row_number: number;
  employee_id: string | null;
  name: string | null;
  date: string | null;
  payable: string | null;
  failure_reason: string;
  error_details?: string | null;
  file_name?: string | null;
  attempted_at?: string | null;
}

export interface FailedDailyPayListResponse {
  data: FailedDailyPayRecord[];
  meta: { total: number; page: number; limit: number; totalPages?: number };
}

export interface FailedDailyPayListParams {
  page: number;
  limit: number;
  job_id?: string;
}

export interface SalaryGradeResponse {
  success: boolean;
  message: string | null;
  data: SalaryGradeData;
}

/* ------------------------------------------------------------------ */
/* Payroll batches                                                     */
/* A batch is one payroll run; each employee in it is a history row.  */
/* ------------------------------------------------------------------ */

/** Whether a row's (or a whole batch's) salary sheet has been produced. */
export type SalarySheetState = 'GENERATED' | 'NOT_GENERATED';

/** Payment state of a batch or a row. A batch can also fail to generate. */
export type PayrollPaymentStatus = 'PAID' | 'UNPAID' | 'FAILED';

/** One batch, as `GET /payroll/batch` lists it. */
export interface PayrollBatchItem {
  id: string;
  tenant_id: string;
  organization_id: string;
  batch_name: string;
  year: number;
  /** Two digits. */
  month: string;
  total_employees: number;
  total_amount: string;
  /** How many rows already have their salary sheet. */
  generated_salary_sheets?: number;
  status: PayrollPaymentStatus;
  salary_sheet: SalarySheetState;
  /** Why generation failed, when `status` is FAILED. */
  error_response?: string | null;
  created_at: string;
  updated_at: string;
  /** JTI: absent on batches created before pay types existed, read as MONTHLY. */
  pay_basis?: PayType | null;
  /** JTI: the cycle a daily batch paid. Null on calendar-month batches. */
  cycle_no?: number | null;
  cycle_start_date?: string | null;
  cycle_end_date?: string | null;
}

export interface PayrollBatchResponse {
  success: boolean;
  message: string;
  data: { data: PayrollBatchItem[]; meta: MetaData };
}

/**
 * Amounts that make up a row's pay, keyed by component name. Daily rows also
 * carry their cycle facts here (`cycle_no`, `daily_rate`, `present_days`,
 * `total_days`, `cycle_start_date`, `cycle_end_date`).
 */
export type PayrollBreakdown = Record<string, number | string | null>;

/** One employee row inside a batch (`GET /payroll/batch/{id}/salary-sheet`). */
export interface PayrollBatchRow {
  id: string;
  batch_id: string;
  user_id: string;
  tenant_id: string;
  organization_id: string;
  employee_id: string;
  employee_name: string;
  department_name: string;
  total_base_salary_structure_amount: string;
  total_grade_benefits_amount: string;
  net_salary: string;
  base_salary_structure_breakdown: PayrollBreakdown | null;
  grade_benefits_breakdown: PayrollBreakdown | null;
  fixed_addition: {
    overtime?: number;
    admin_bonus?: number;
    leave_encashment?: number;
  } | null;
  fixed_deduction: { admin_deduction?: number } | null;
  /** Policy bonuses disbursed into this row. */
  bonus_addition: { title: string; amount: number; policy_id: string }[] | null;
  /** JTI: set by the adjustments upload or the row editor. */
  tax_amount?: string | number | null;
  adjustment_note?: string | null;
  /** Not sent by the server yet; daily facts live in the structure breakdown. */
  daily_pay_breakdown?: DailyPayBreakdown | null;
  status: 'PAID' | 'UNPAID';
  salary_sheet: SalarySheetState;
  payment_date?: string | null;
  created_at: string;
  updated_at: string;
}

export interface PayrollByBatchResponse {
  success: boolean;
  message: string;
  data: { data: PayrollBatchRow[]; meta: MetaData };
}

/**
 * `GET /payroll/batch/{id}`: the batch itself. `history` holds every employee
 * row of the batch, unpaginated, which the salary-sheet list does not.
 */
export interface SinglePayrollBatchResponse {
  success: boolean;
  message: string | null;
  data: {
    id: string;
    batch_name: string;
    year: number;
    month: string;
    total_employees: number;
    salary_sheet: SalarySheetState;
    status: string;
    pay_basis?: PayType | null;
    cycle_no?: number | null;
    history?: { id: string; salary_sheet: SalarySheetState }[];
  };
}

/**
 * Body for `POST /payroll/history/update-component`. Only the fields that are
 * sent change; the rest of the row keeps its values.
 */
export interface UpdatePayrollComponentBody {
  /** Payroll row (history) id. */
  id: string;
  admin_bonus?: number;
  admin_deduction?: number;
  tax?: number;
  note?: string;
}

/** One rejected row from an adjustments Excel upload. */
export interface FailedAdjustmentRecord {
  id: string;
  job_id: string;
  row_number: number;
  employee_id: string | null;
  name: string | null;
  addition: string | null;
  deduction: string | null;
  tax: string | null;
  failure_reason: string;
  error_details?: string | null;
  file_name?: string | null;
  attempted_at?: string | null;
}

export interface FailedAdjustmentListResponse {
  data: FailedAdjustmentRecord[];
  meta: { total: number; page: number; limit: number; totalPages?: number };
}

export interface FailedAdjustmentListParams {
  page: number;
  limit: number;
  job_id?: string;
}

/** One row of the bank list (`GET /payroll/batch/{id}/bank-list`). */
export interface BankListRow {
  id: string;
  department_name: string;
  employee_id: string;
  employee_name: string;
  net_salary: string;
  bank_name: string | null;
  account_name: string | null;
  account_number: string | null;
  branch: string | null;
  routing_number: string | null;
  status: 'UNPAID' | 'PAID';
}

export interface BatchSalarySheetResponse {
  success: boolean;
  message: string;
  data: { data: BankListRow[]; meta: MetaData };
}

/** Body for `POST /payroll/history/payment-status`. */
export interface UpdatePaymentStatusBody {
  ids: string[];
  status: 'PAID' | 'UNPAID';
}

export interface PayrollBatchParams extends CommonParams {
  year?: string;
  month?: string;
  /** JTI: filter batches by population. */
  pay_type?: PayType;
  /** JTI: filter by resolved cycle instead of year/month. */
  period_start_from?: string;
  period_end_to?: string;
}

/**
 * Body for `POST /payroll/generate`.
 * `year` + `month` are ALWAYS sent, even for a custom cycle, so a plain
 * monthly run looks exactly as it did before the payroll calendar.
 */
export interface GenerateSalaryBody {
  payFor: PayType;
  year: number;
  /** Two digits. Required for MONTHLY; always sent by the form. */
  month: string;
  userIds: string[];
  /**
   * DAILY only: one cycle of the month, from `GET /payroll-calendar/cycles`.
   * Left out, the server generates every cycle of the month.
   */
  cycleNo?: number;
}

/** JTI: how a daily employee's pay was reached, shown in the batch row. */
export interface DailyPayBreakdown {
  payable_days: number;
  daily_rate: number;
  amount: number;
}
export interface BatchSalarySheetParams extends CommonParams {}
export interface PayrollsByBatchParams extends CommonParams {}

export interface EncashmentBatchParams extends CommonParams {
  year?: number;
}

/* ------------------------------------------------------------------ */
/* Leave encashment                                                    */
/* A batch pays unused leave for one fiscal year; each employee is a   */
/* record that is later disbursed into a payroll month.                */
/* ------------------------------------------------------------------ */

/**
 * NOT_GENERATED can still be recalculated or deleted; GENERATED can be
 * disbursed; FAILED carries `failed_reason`.
 */
export type EncashmentBatchStatus =
  | 'GENERATED'
  | 'NOT_GENERATED'
  | 'PENDING'
  | 'FAILED';

/** Only a PENDING record can be edited or disbursed. */
export type EncashmentRecordStatus = 'PENDING' | 'DISBURSED' | 'FAILED';

/** One leave type's share of a record. */
export interface EncashmentBreakdownItem {
  leave_type_id: string;
  leave_name: string;
  days: number;
  rate_basis: string;
  per_day_rate: number;
  percentage: number;
  amount: number;
}

/** The batch fields shared by the list and the details endpoints. */
export interface EncashmentBatchItem {
  id: string;
  tenant_id: string;
  organization_id: string;
  batch_name: string;
  /** ISO date (YYYY-MM-DD). */
  fiscal_year_start: string;
  /** ISO date (YYYY-MM-DD). */
  fiscal_year_end: string;
  status: EncashmentBatchStatus;
  total_employees: number;
  /** Kept as a string to avoid floating point issues. */
  total_amount: string;
  failed_reason: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

/** One employee inside an encashment batch. */
export interface EncashmentRecord {
  id: string;
  batch_id: string;
  user_id: string;
  tenant_id: string;
  organization_id: string;
  total_amount: string;
  status: EncashmentRecordStatus;
  encashment_breakdown: EncashmentBreakdownItem[];
  disbursed_at_batch_id: string | null;
  disbursed_at_date: string | null;
  disbursed_for_month_year: string | null;
  note: string | null;
  created_at: string;
  updated_at: string;
  employee: {
    name: string;
    employee_id: string;
    office_id: string;
    department: { name: string };
    designation: { name: string };
  };
}

/** A batch with one page of its records. */
export interface EncashmentBatchDetail extends EncashmentBatchItem {
  records: EncashmentRecord[];
  _count: { records: number };
  records_meta: { total: number; skip: number; limit: number };
}

export interface EncashmentBatchResponse {
  success: boolean;
  message: string;
  data: { data: EncashmentBatchItem[]; total: number };
}

/** `GET /leave-encashment/batches/{id}`: every record, pending ones included. */
export interface EncashmentByBatchResponse {
  success: boolean;
  message: string;
  data: EncashmentBatchDetail;
}

/** `GET /leave-encashment/batches/{id}/disbursed`: records that left PENDING. */
export type DisburseRecordByBatchResponse = EncashmentByBatchResponse;

/** Params shared by both record lists. */
export interface EncashmentRecordsParams extends CommonParams {
  office_id?: string;
}

/** Body for `POST /leave-encashment/generate-batch`. */
export interface GenerateEncashmentBody {
  fiscal_year: number;
  user_ids: string[];
}

/** Body for `PATCH /leave-encashment/batches/{batchId}/records/{recordId}`. */
export interface UpdateEncashmentRecordBody {
  note?: string;
  encashment_breakdown?: EncashmentBreakdownItem[];
}

/** Body for `POST /leave-encashment/disburse`. */
export interface DisburseEncashmentBody {
  batch_id: string;
  year: number;
  /** Two digits. */
  month: string;
  user_ids: string[];
}

export interface FiscalYearItem {
  ui: string;
  payload: number;
}

export interface FiscalYearsResponse {
  success: boolean;
  message: string | null;
  data: FiscalYearItem[];
}

export interface DisbursableEmployee {
  id: string;
  photo: string | null;
  name: string;
  user_id: string;
  employee_id: string;
  status: string;
  department: {
    id: string;
    name: string;
  };
  designation: {
    id: string;
    name: string;
  };
}

export interface DisbursableEmployeesData {
  message: string;
  data: DisbursableEmployee[];
  currentLength: number;
  totalLength: number;
}

export interface DisbursableEmployeesParams {
  search?: string;
  designation_id?: string;
  department_id?: string;
  status?: string;
}

export interface DisbursableEmployeesResponse {
  success: boolean;
  message: string | null;
  data: DisbursableEmployeesData;
}

// Bonus Policy Types

/**
 * Who a bonus policy is for: monthly employees, daily employees, or both.
 * Policies saved before this existed carry none; the server applies its own
 * default to them, so the frontend never sends a value it was not given.
 */
export type BonusPayBasis = 'ALL' | PayType;

export const BONUS_PAY_BASIS_OPTIONS: {
  label: string;
  value: BonusPayBasis;
}[] = [
  { label: 'All Employees', value: 'ALL' },
  { label: 'Monthly', value: 'MONTHLY' },
  { label: 'Daily', value: 'DAILY' },
];

export interface BonusPolicyItem {
  id: string;
  organization_id: string;
  tenant_id: string;
  title: string;
  amount_flat: string | null;
  amount_percentage: string | null;
  amount_percentage_of: string | null;
  count_per_fiscal_year: number;
  /** JTI: absent on policies saved before pay basis existed. */
  pay_basis?: BonusPayBasis | null;
  employee_types: string[];
  user_ids: string[];
  status: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface BonusPolicyDetail extends BonusPolicyItem {
  users: {
    user_id: string;
    name: string;
    employee_id: string;
    photo: string | null;
    disbursed_count: number;
  }[];
}

export interface AllBonusPoliciesResponse {
  success: boolean;
  message: string | null;
  data: {
    data: BonusPolicyItem[];
    meta: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  };
}

export interface SingleBonusPolicyResponse {
  success: boolean;
  message: string | null;
  data: BonusPolicyDetail;
}

export interface BonusPoliciesParams extends CommonParams {
  status?: string;
  employeeType?: string;
  payBasis?: BonusPayBasis;
}

/** Body for creating (`POST`) or updating (`PATCH`) a bonus policy. */
export interface BonusPolicyBody {
  title: string;
  status: 'active' | 'inactive';
  /** Sent as 0 when the policy pays a percentage. */
  amountFlat: number;
  /** Sent as 0 when the policy pays a flat amount. */
  amountPercentage: number;
  /** Salary component the percentage is taken from, or null for flat. */
  amountPercentageOf: string | null;
  countPerFiscalYear: number;
  /** Left out when the admin never chose one (legacy policies). */
  payBasis?: BonusPayBasis;
  employeeTypes: string[];
  userIds: string[];
}

/** Body for `POST /bonus/disburse`. */
export interface DisburseBonusBody {
  policyId: string;
  year: number;
  /** Two digits. */
  month: string;
  /** How many times the bonus is paid in this disbursement (usually 1). */
  count: number;
  /** Which payroll batches receive it. The server defaults to MONTHLY. */
  payBasis?: PayType;
  /** DAILY only: one cycle of the month, from `GET /payroll-calendar/cycles`. */
  cycleNo?: number;
  /** Left out, the server pays every eligible employee. */
  userIds?: string[];
}

// ------ START: Failed Payroll Types ------
export interface FailedPayrollRecord {
  id: string;
  job_id: string;
  row_number: number;
  organization_id: string;
  tenant_id: string;
  year: number;
  month: string;
  name: string;
  email: string;
  employee_id: string | null;
  amount: string;
  status: string;
  failure_reason: string;
  error_details: any | null;
  file_name: string;
  attempted_at: string;
  uploaded_by: string;
  is_resolved: boolean;
  resolved_at: string | null;
  resolved_by: string | null;
  created_at: string;
  updated_at: string;
}

export type FailedPayrollListResponse = {
  data: FailedPayrollRecord[];
  meta: {
    total: number;
    page: number;
    last_page: number;
    limit: number;
  };
};

export type FailedPayrollListParams = {
  page: number;
  limit: number;
  job_id?: string;
};
// ------ END: Failed Payroll Types ------

